import { useState } from 'react'
import { Box, Button, Chip, Tab, Typography } from '@mui/material'
import { TabContext, TabList } from '@mui/lab'
import SaveIcon from '@mui/icons-material/Save'
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent'
import { Page } from 'src/admin/core/components'
import { useAppTranslation } from 'src/admin/core/hooks'
import ConfigurarAssignments from './tabs/ConfigurarAssignments'
import AsociarDisplays from './tabs/AsociarDisplays'
import ConfiguracionGeneral from './tabs/ConfiguracionGeneral'
import { ScreensProvider, useScreensContext } from './ScreensContext'
import { CircularProgress } from '@mui/material'

// Inner component so it can consume the context
const ScreensPageInner = () => {
  const { label } = useAppTranslation()
  const [activeTab, setActiveTab] = useState('1')
  const { hasChanges, hasValidationErrors, saving, onSave } = useScreensContext()

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue)
  }

  const strEnterprise = window.sessionStorage.getItem('selectedEnterprise')
  const enterprise = strEnterprise ? JSON.parse(strEnterprise) : null
  const enterpriseName = enterprise?.name || 'Consorcio'

  const now = new Date()
  const formattedDate = now.toLocaleDateString('es-DO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
  const formattedTime = now.toLocaleTimeString('es-DO', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
  return (
    <Page
      pageTitle={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <SettingsInputComponentIcon color="primary" />
          <Box>
            <Typography variant="h3" component="h3" sx={{ fontWeight: 700, mb: 0 }}>
              {label('ADMINISTRAR_SCREENS')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                {label('ADMINISTRAR_SCREENS_SUBTITULO')}
              </Typography>
              <Chip
                label={enterpriseName}
                size="small"
                color="info"
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: '0.65rem', height: 20 }}
              />
            </Box>
          </Box>
        </Box>
      }
      actions={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {formattedDate}, {formattedTime}
          </Typography>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            disabled={!hasChanges || saving || hasValidationErrors}
            onClick={onSave}
            sx={{ textTransform: 'none' }}
          >
            {label('GUARDAR_CAMBIOS')}
          </Button>
        </Box>
      }
    >
      <Box sx={{ width: '100%', px: 3 }}>
        <TabContext value={activeTab}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
            <TabList
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                },
              }}
            >
              <Tab label={label('CONFIGURAR_ASSIGNMENTS')} value="1" />
              <Tab label={label('ASOCIAR_A_DISPLAYS')} value="2" />
              <Tab label={label('CONFIGURACION_GENERAL_TAB')} value="3" />
            </TabList>
          </Box>
          {/* Keep all tabs mounted to preserve state; toggle visibility with CSS */}
          <Box sx={{ py: 3, display: activeTab === '1' ? 'block' : 'none' }}>
            <ConfigurarAssignments isActive={activeTab === '1'} />
          </Box>
          <Box sx={{ py: 3, display: activeTab === '2' ? 'block' : 'none' }}>
            <AsociarDisplays />
          </Box>
          <Box sx={{ py: 3, display: activeTab === '3' ? 'block' : 'none' }}>
            <ConfiguracionGeneral />
          </Box>
        </TabContext>
      </Box>
    </Page>
  )
}

const ScreensPage = () => (
  <ScreensProvider>
    <ScreensPageInner />
  </ScreensProvider>
)

export default ScreensPage
